import Dexie, { type Table } from 'dexie';

import db from '../../lib/db';
import {
  type EntityTargets,
  buildChannelSnapshot,
  buildEmojiSnapshot,
  buildProfileSnapshot,
  entityTargetsFromMessageData,
  extractEntityTargets,
} from '../../lib/entitySnapshotBuilders';
import { logError } from '../../lib/logger';

import { type BufferWindow, type FeedBufferLike, type FeedPageMergeResult, type PrunePolicy } from '../core/buffer';
import {
  type FeedPageMergeRequest,
  type FeedSegment,
  type LocalPageResult,
  coalesceOverlappingSegments,
  compareSyncCursors,
  decideAfter,
  decideAround,
  decideBefore,
  findContainingSegment,
  markSegmentStale,
  maxSyncCursor,
  mergeSegments,
  noSegmentMiss,
  resegmentAfterPrune,
  segmentFromPage,
  segmentId,
} from '../core/coverage';
import { FeedMirror, type FeedMirrorHydration, type WarmCoverageRange } from '../core/feedMirror';
import { isStorageFailure } from '../core/storageError';
import { compareSortOrders } from '../core/ids';
import type {
  ChannelId,
  ClientRequestId,
  EntityBundle,
  FeedPage,
  MessageId,
  SortOrder,
  SyncCursor,
  SyncEvent,
  WireAttachment,
  WireMediaVariant,
  WireMessage,
  WorkspaceId,
} from '../core/types';
import {
  coverageFromAfterRead,
  coverageFromBeforeRead,
  coverageFromMergeRequest,
  coverageFromMessages,
  warmCoverageRange,
} from '../core/warmCoverage';
import { ECHO_PLACEHOLDER_AUTHOR_FLAG } from './project';
import { GlobalWriteQueue } from './globalWriteQueue';
import {
  type EchoAuthorType,
  type EchoChatPreview,
  type EchoWireAttachment,
  type EchoWireLinkPreview,
  type EchoWireMention,
  type EchoWireMessage,
  type EchoWireReaction,
  parseEchoWireMessage,
} from './wire';

const DEFAULT_WARM_ROW_LIMIT = 400;
const DEFAULT_RESIDENT_CONVERSATION_LIMIT = 32;
const UNKNOWN_WORKSPACE_ID = '__message_feed_unknown_workspace__';

/** Constructor options for the Echo Dexie-backed feed buffer. */
export interface EchoDexieBufferOptions {
  /** Override database instance for isolated tests; defaults to the app Dexie singleton. */
  readonly database?: EchoDexieDatabase;
  /** Start unhealthy so durable reads miss and writes no-op. */
  readonly failOpen?: boolean;
  /** Simulate durable write failures while keeping read paths available. */
  readonly failWrites?: boolean;
  /** Called when durable storage health changes. */
  readonly onHealthChange?: (healthy: boolean) => void;
  /** Called after committed message row changes so non-feed projections can follow them. */
  readonly onMessageRowsChanged?: (changes: {
    readonly upserts: readonly Record<string, unknown>[];
    readonly deletes: readonly Record<string, unknown>[];
    readonly confirmedDeletes: readonly Record<string, unknown>[];
  }) => Promise<void>;
  /** Maximum number of conversation mirrors retained in memory. */
  readonly residentConversationLimit?: number;
  /** Maximum warm rows retained per mirrored conversation. */
  readonly warmRowLimit?: number;
}

/** Thrown when a security-sensitive feed cleanup cannot complete. */
export class EchoDexieCleanupError extends Error {
  readonly cause: unknown;

  constructor(operation: string, cause: unknown) {
    super(`Echo Dexie ${operation} failed`);
    this.name = 'EchoDexieCleanupError';
    this.cause = cause;
  }
}

interface EchoDexieDatabase extends Dexie {
  readonly channels: Table<Record<string, unknown>, [WorkspaceId, string]>;
  readonly emojis: Table<Record<string, unknown>, [WorkspaceId, string]>;
  readonly feed_coverage: Table<EchoCoverageIntervalRow, string>;
  readonly feed_messages: Table<EchoMessageRow, string>;
  readonly message_feed_meta: Table<EchoFeedMetaRow, [WorkspaceId, ChannelId]>;
  readonly profiles: Table<Record<string, unknown>, [WorkspaceId, string]>;
}

interface EchoMessageRow extends Record<string, unknown> {
  readonly id: MessageId;
  readonly workspace_id: WorkspaceId;
  readonly channel_id: ChannelId;
  readonly text: string;
  readonly sort_order: SortOrder;
  readonly timestamp: number;
  readonly author_id: string | null;
  readonly author_type: EchoAuthorType | null;
  readonly flags?: readonly string[];
  readonly attachments: readonly Record<string, unknown>[];
  readonly reactions: readonly Record<string, unknown>[];
  readonly mentions: readonly Record<string, unknown>[];
  readonly link_previews: readonly Record<string, unknown>[];
  readonly contains_only_attachments: boolean;
  readonly contains_only_entities: boolean;
  readonly contains_only_emoji_entities: boolean;
  readonly fresh_since?: number;
  readonly optimistic_sort_order?: ClientRequestId;
  readonly reply_to_message_id?: MessageId;
  readonly type?: string;
  readonly data?: Record<string, unknown>;
  readonly actors?: readonly Record<string, unknown>[];
  readonly source_widget_id?: string;
  readonly source_widget?: Record<string, unknown>;
  readonly _feed_mutation_cursor?: SyncCursor;
}

interface EchoCoverageIntervalRow {
  readonly id: string;
  readonly workspace_id: WorkspaceId;
  readonly channel_id: ChannelId;
  readonly lower_sort_order: SortOrder;
  readonly upper_sort_order: SortOrder;
  readonly reaches_start?: boolean;
  readonly reaches_live_tail?: boolean;
  readonly reaches_oldest?: boolean;
  readonly reaches_latest?: boolean;
  readonly stale?: boolean;
  readonly fetched_at: number;
}

interface EchoFeedMetaRow {
  readonly workspace_id: WorkspaceId;
  readonly channel_id: ChannelId;
  readonly sync_cursor?: SyncCursor;
  readonly verified_sync_cursor?: SyncCursor;
  readonly bootstrap_sync_cursor?: SyncCursor;
  readonly live_tail_id?: MessageId;
  readonly live_tail_sort_order?: SortOrder;
  readonly meta_revision?: number;
  readonly updated_at: number;
}

interface EchoReadHydration extends FeedMirrorHydration {
  readonly coverage: WarmCoverageRange[];
  readonly messages: EchoWireMessage[];
  readonly result?: LocalPageResult;
  readonly segments: FeedSegment[];
}

/** Durable feed buffer adapter over echo's feed-owned Dexie tables. */
export class EchoDexieBuffer implements FeedBufferLike {
  private readonly database: EchoDexieDatabase;
  private readonly membershipGenerations = new Map<string, number>();
  private readonly mirror: FeedMirror;
  private readonly onHealthChange: ((healthy: boolean) => void) | undefined;
  private readonly onMessageRowsChanged: EchoDexieBufferOptions['onMessageRowsChanged'];
  private readonly reportedMissingProfileAuthors = new Set<string>();
  private readonly workspaceEntityRevisions = new Map<string, number>();
  private readonly writeQueue = new GlobalWriteQueue();
  private healthy: boolean;
  private simulateWriteFailure: boolean;

  constructor(options: EchoDexieBufferOptions = {}) {
    const residentConversationLimit = Math.max(1, Math.floor(options.residentConversationLimit ?? DEFAULT_RESIDENT_CONVERSATION_LIMIT));
    const warmRowLimit = Math.max(0, Math.floor(options.warmRowLimit ?? DEFAULT_WARM_ROW_LIMIT));
    // SAFETY: lib/db owns these feed tables at runtime; tests can inject the same narrowed shape.
    this.database = options.database ?? (db as EchoDexieDatabase);
    this.healthy = options.failOpen !== true;
    this.mirror = new FeedMirror({
      requireCompleteEntities: false,
      residentConversationLimit,
      warmRowLimit,
    });
    this.onHealthChange = options.onHealthChange;
    this.onMessageRowsChanged = options.onMessageRowsChanged;
    this.simulateWriteFailure = options.failWrites === true;
  }

  /** Returns whether durable reads/writes are currently trusted. */
  isHealthy(): boolean {
    return this.healthy;
  }

  /** Toggles simulated durable write failure for debug controls/tests. */
  setSimulateWriteFailure(failWrites: boolean): void {
    this.simulateWriteFailure = failWrites;
  }

  /** Returns the current membership generation for stale queued-write fencing. */
  membershipGeneration(accountId: string): number {
    return this.membershipGenerations.get(accountId) ?? 0;
  }

  /** Returns the in-memory entity revision for a workspace's loaded rows. */
  entityRevision(accountId: string, workspaceId: WorkspaceId): number {
    return this.workspaceEntityRevisions.get(workspaceEntityRevisionKey(accountId, workspaceId)) ?? 0;
  }

  /** Reads older rows synchronously from the warm mirror when coverage is resident. */
  beforeSync(accountId: string, conversationId: ChannelId, boundary: SortOrder, limit: number): LocalPageResult | undefined {
    if (!this.healthy) return undefined;
    return this.mirror.readBefore(accountId, conversationId, boundary, normalizedLimit(limit));
  }

  /** Reads newer rows synchronously from the warm mirror when coverage is resident. */
  afterSync(accountId: string, conversationId: ChannelId, boundary: SortOrder, limit: number): LocalPageResult | undefined {
    if (!this.healthy) return undefined;
    return this.mirror.readAfter(accountId, conversationId, boundary, normalizedLimit(limit));
  }

  /** Reads the latest rows synchronously from the warm mirror when coverage is resident. */
  latestSync(accountId: string, conversationId: ChannelId, limit: number): LocalPageResult | undefined {
    if (!this.healthy) return undefined;
    return this.mirror.readLatest(accountId, conversationId, normalizedLimit(limit));
  }

  /** Reads the persisted sync cursor for a conversation, if one exists. */
  async readSyncCursor(_accountId: string, conversationId: ChannelId): Promise<SyncCursor | undefined> {
    return this.read(
      async () => verifiedSyncCursor(await this.readFeedMeta(conversationId)),
      () => undefined,
    );
  }

  /** Persists a monotonic sync cursor and updates mirror metadata after commit. */
  async persistSyncCursor(accountId: string, conversationId: ChannelId, cursor: SyncCursor, membershipGeneration: number): Promise<void> {
    await this.writeQueue.enqueue(accountId, conversationId, async () => {
      let mirrorSegments: FeedSegment[] | undefined;
      let metaRevision: number | undefined;
      let revoked = false;
      let syncCursor: SyncCursor | undefined;
      const persisted = await this.write(async () => {
        await this.database.transaction(
          'rw',
          [this.database.channels, this.database.feed_messages, this.database.feed_coverage, this.database.message_feed_meta],
          async () => {
            if (this.isStaleMembership(accountId, membershipGeneration)) {
              revoked = true;
              return;
            }

            const workspaceId = await this.resolveWorkspaceId(conversationId, []);
            const meta = await this.readFeedMeta(conversationId);
            const previousCursor = verifiedSyncCursor(meta) ?? '';
            syncCursor = maxSyncCursor(previousCursor, cursor);
            if (meta?.verified_sync_cursor !== undefined && syncCursor === previousCursor) return;

            await this.advanceSyncCursor(conversationId, workspaceId, syncCursor);
            syncCursor = (await this.readFeedMeta(conversationId))?.sync_cursor ?? syncCursor;
            mirrorSegments = await this.readConversationSegments(accountId, conversationId);
            metaRevision = await this.bumpMetaRevision(conversationId, workspaceId);
          },
        );
        return true;
      });
      if (!persisted || revoked || !mirrorSegments || metaRevision === undefined || syncCursor === undefined) return;
      this.mirror.applyCommittedSegments(accountId, conversationId, mirrorSegments, syncCursor, metaRevision);
    });
  }

  /** Drops a resident mirror when durable metadata has advanced behind it. */
  async revalidateMirror(accountId: string, conversationId: ChannelId): Promise<void> {
    const durable = await this.read(
      async () => {
        const meta = await this.readFeedMeta(conversationId);
        return meta ? { present: true, revision: meta.meta_revision ?? 0 } : { present: false, revision: 0 };
      },
      () => undefined,
    );
    const mirrorRevision = this.mirror.metaRevision(accountId, conversationId);
    if (!durable?.present || mirrorRevision === undefined || mirrorRevision !== durable.revision) {
      this.mirror.dropConversation(accountId, conversationId);
    }
  }

  /** Reads the latest durable window and hydrates the warm mirror. */
  async readLatest(accountId: string, conversationId: ChannelId, limit: number): Promise<BufferWindow> {
    const count = normalizedLimit(limit);
    const hydration = await this.read<EchoReadHydration>(
      async () =>
        this.database.transaction(
          'r',
          [
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.channels,
            this.database.emojis,
          ],
          async () => {
            const messages = await this.readLatestMessages(conversationId, count);
            const segments = await this.readConversationSegments(accountId, conversationId);
            const meta = await this.readFeedMeta(conversationId);
            return {
              coverage: coverageFromMessages(messages),
              entities: emptyEntityBundle(),
              metaRevision: meta?.meta_revision ?? 0,
              messages,
              segments,
              syncCursor: meta?.sync_cursor ?? '',
            };
          },
        ),
      () => emptyReadHydration(),
    );
    this.mirror.hydrate(accountId, conversationId, hydration);
    return {
      entities: hydration.entities,
      messages: hydration.messages,
    };
  }

  /** Reads durable rows centered on an anchor and warms the mirror. */
  async readAround(
    accountId: string,
    conversationId: ChannelId,
    anchor: { readonly id: MessageId; readonly sortOrder: SortOrder },
    limit: number,
  ): Promise<LocalPageResult> {
    const count = normalizedLimit(limit);
    const hydration = await this.read<EchoReadHydration>(
      async () =>
        this.database.transaction(
          'r',
          [
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.channels,
            this.database.emojis,
          ],
          async () => {
            const segments = await this.readConversationSegments(accountId, conversationId);
            const meta = await this.readFeedMeta(conversationId);
            const syncCursor = meta?.sync_cursor ?? '';
            const segment = findContainingSegment(segments, anchor.sortOrder);
            const base = emptyReadHydration();
            const withMeta = {
              ...base,
              metaRevision: meta?.meta_revision ?? 0,
              segments,
              syncCursor,
            };
            if (!segment || segment.stale) {
              return {
                ...withMeta,
                result: decideAround(segment, [], emptyEntityBundle(), syncCursor, anchor),
              };
            }

            const messages = await this.readMessagesAround(conversationId, segment, anchor.sortOrder, count);
            return {
              coverage: coverageFromMessages(messages),
              entities: emptyEntityBundle(),
              metaRevision: meta?.meta_revision ?? 0,
              messages,
              result: decideAround(segment, messages, emptyEntityBundle(), syncCursor, anchor),
              segments,
              syncCursor,
            };
          },
        ),
      () => emptyReadHydration(noSegmentMiss()),
    );
    this.mirror.hydrate(accountId, conversationId, hydration);
    return hydration.result ?? noSegmentMiss();
  }

  /** Reads an older durable page relative to the boundary sort order. */
  async before(accountId: string, conversationId: ChannelId, boundary: SortOrder, limit: number): Promise<LocalPageResult> {
    const count = normalizedLimit(limit);
    const hydration = await this.read<EchoReadHydration>(
      async () =>
        this.database.transaction(
          'r',
          [
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.channels,
            this.database.emojis,
          ],
          async () => {
            const segments = await this.readConversationSegments(accountId, conversationId);
            const meta = await this.readFeedMeta(conversationId);
            const syncCursor = meta?.sync_cursor ?? '';
            const segment = findContainingSegment(segments, boundary);
            const base = emptyReadHydration();
            const withMeta = {
              ...base,
              metaRevision: meta?.meta_revision ?? 0,
              segments,
              syncCursor,
            };
            if (!segment || segment.stale) {
              return {
                ...withMeta,
                result: decideBefore(segment, [], emptyEntityBundle(), syncCursor),
              };
            }

            const messages = await this.readMessagesBefore(conversationId, segment, boundary, count);
            const coverage = coverageFromBeforeRead(segment, boundary, messages, count);
            return {
              coverage,
              entities: emptyEntityBundle(),
              metaRevision: meta?.meta_revision ?? 0,
              messages,
              result: decideBefore(segment, messages, emptyEntityBundle(), syncCursor),
              segments,
              syncCursor,
            };
          },
        ),
      () => emptyReadHydration(noSegmentMiss()),
    );
    this.mirror.hydrate(accountId, conversationId, hydration);
    return hydration.result ?? noSegmentMiss();
  }

  /** Reads a newer durable page relative to the boundary sort order. */
  async after(accountId: string, conversationId: ChannelId, boundary: SortOrder, limit: number): Promise<LocalPageResult> {
    const count = normalizedLimit(limit);
    const hydration = await this.read<EchoReadHydration>(
      async () =>
        this.database.transaction(
          'r',
          [
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.channels,
            this.database.emojis,
          ],
          async () => {
            const segments = await this.readConversationSegments(accountId, conversationId);
            const meta = await this.readFeedMeta(conversationId);
            const syncCursor = meta?.sync_cursor ?? '';
            const segment = findContainingSegment(segments, boundary);
            const base = emptyReadHydration();
            const withMeta = {
              ...base,
              metaRevision: meta?.meta_revision ?? 0,
              segments,
              syncCursor,
            };
            if (!segment || segment.stale) {
              return {
                ...withMeta,
                result: decideAfter(segment, [], emptyEntityBundle(), syncCursor),
              };
            }

            const messages = await this.readMessagesAfter(conversationId, segment, boundary, count);
            const coverage = coverageFromAfterRead(segment, boundary, messages, count);
            return {
              coverage,
              entities: emptyEntityBundle(),
              metaRevision: meta?.meta_revision ?? 0,
              messages,
              result: decideAfter(segment, messages, emptyEntityBundle(), syncCursor),
              segments,
              syncCursor,
            };
          },
        ),
      () => emptyReadHydration(noSegmentMiss()),
    );
    this.mirror.hydrate(accountId, conversationId, hydration);
    return hydration.result ?? noSegmentMiss();
  }

  /** Merges a fetched page into feed-owned Dexie tables and the warm mirror. */
  async mergePage(
    accountId: string,
    conversationId: ChannelId,
    request: FeedPageMergeRequest,
    page: FeedPage,
    membershipGeneration: number,
  ): Promise<FeedPageMergeResult> {
    return this.writeQueue.enqueue(accountId, conversationId, async () => {
      const removedIds: MessageId[] = [];
      let removedRows: EchoMessageRow[] = [];
      let mirrorCoverage: WarmCoverageRange[] = [];
      let mirrorMessages: EchoWireMessage[] = [];
      let mirrorSegments: FeedSegment[] = [];
      let metaRevision: number | undefined;
      let upsertedRows: EchoMessageRow[] = [];
      let revoked = false;
      let syncCursor = page.syncCursor;
      const persisted = await this.write(async () => {
        await this.database.transaction(
          'rw',
          [
            this.database.channels,
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.emojis,
          ],
          async () => {
            if (this.isStaleMembership(accountId, membershipGeneration)) {
              revoked = true;
              return;
            }

            const workspaceId = await this.resolveWorkspaceId(conversationId, page.messages);
            // The first page establishes coverage. Include any earlier live-only
            // observations, and don't skip events newer than an in-flight snapshot.
            const checkpoint = feedSyncCheckpoint(await this.readFeedMeta(conversationId));
            if (checkpoint.kind === 'cold' || checkpoint.kind === 'bootstrap') {
              const initialCursor = checkpoint.kind === 'bootstrap'
                ? earlierSyncCursor(checkpoint.cursor, page.syncCursor)
                : page.syncCursor;
              await this.advanceSyncCursor(conversationId, workspaceId, initialCursor);
            }
            syncCursor = await this.advanceObservedCursor(conversationId, workspaceId, page.syncCursor);
            if (page.messages.length > 0) {
              upsertedRows = await this.putPageMessages(page.messages, page.syncCursor);
            }

            const pageSegment = segmentFromPage(accountId, conversationId, page);
            if (pageSegment) {
              removedRows = await this.reconcileAbsentMessages(conversationId, pageSegment, page.messages, page.syncCursor);
              removedIds.push(...removedRows.map((row) => row.id));

              const segments = await this.readConversationSegments(accountId, conversationId);
              const nextSegments = await this.normalizeSegments(mergeSegments(segments, request, pageSegment));
              await this.replaceConversationSegments(conversationId, nextSegments, workspaceId);
              await this.updateLiveTailMeta(conversationId, workspaceId, nextSegments);
              mirrorMessages = await this.readMessagesInRange(conversationId, pageSegment.oldestSortOrder, pageSegment.newestSortOrder);
              mirrorCoverage = coverageFromMergeRequest(request, pageSegment);
            }

            mirrorSegments = await this.readConversationSegments(accountId, conversationId);
            metaRevision = await this.bumpMetaRevision(conversationId, workspaceId);
          },
        );
        return true;
      });
      if (revoked) return { persisted: false, removedIds: [], revoked: true };
      if (!persisted || metaRevision === undefined) return { persisted: false, removedIds: [], revoked: false };

      this.bumpEntityRevisions(accountId, page.messages, page.entities);
      this.mirror.applyCommittedSegments(accountId, conversationId, mirrorSegments, syncCursor, metaRevision);
      this.mirror.dropRows(accountId, conversationId, removedIds);
      this.mirror.addRows(accountId, conversationId, mirrorMessages, emptyEntityBundle(), mirrorCoverage);
      await this.notifyMessageRowsChanged({ upserts: upsertedRows, deletes: removedRows, confirmedDeletes: [] });
      return { persisted: true, removedIds, revoked: false };
    });
  }

  /** Persists one sync/live event and mirrors the committed row/coverage changes. */
  async persistEvent(accountId: string, conversationId: ChannelId, event: SyncEvent, membershipGeneration: number): Promise<void> {
    if (event.type === 'entity.updated') {
      this.bumpEntityRevisionsForWorkspaceIds(accountId, await this.readConversationWorkspaceIds(conversationId), event.entities);
      return;
    }

    await this.writeQueue.enqueue(accountId, conversationId, async () => {
      let liveTailPreviousSortOrder: SortOrder | undefined;
      let mirrorMessages: EchoWireMessage[] = [];
      let mirrorSegments: FeedSegment[] = [];
      let metaRevision: number | undefined;
      let removedIds: MessageId[] = [];
      let removedRow: EchoMessageRow | undefined;
      let upsertedRow: EchoMessageRow | undefined;
      let revoked = false;
      let syncCursor = event.cursor;
      const persisted = await this.write(async () => {
        await this.database.transaction(
          'rw',
          [
            this.database.channels,
            this.database.feed_messages,
            this.database.feed_coverage,
            this.database.message_feed_meta,
            this.database.profiles,
            this.database.emojis,
          ],
          async () => {
            if (this.isStaleMembership(accountId, membershipGeneration)) {
              revoked = true;
              return;
            }

            const workspaceId = await this.resolveWorkspaceId(conversationId, event.message ? [event.message] : []);
            if (event.type === 'message.deleted' && event.hardDeleted) {
              const existingById = event.messageId ? await this.database.feed_messages.get(event.messageId) : undefined;
              const existing =
                existingById ??
                (await this.database.feed_messages.where('[channel_id+sort_order]').equals([conversationId, event.sortOrder]).first());
              if (existing) {
                await this.database.feed_messages.delete(existing.id);
                removedIds = [existing.id];
                removedRow = existing;
                const segments = await this.readConversationSegments(accountId, conversationId);
                const nextSegments = await this.normalizeSegments(segments);
                await this.replaceConversationSegments(conversationId, nextSegments, workspaceId);
                await this.updateLiveTailMeta(conversationId, workspaceId, nextSegments);
              }
            } else if (event.message) {
              const existing = await this.database.feed_messages.get(event.message.id);
              if (!existing || !isExistingRowFresher(existing, event.message, event.cursor)) {
                const nextRow = storedEchoMessage(event.message, event.cursor);
                await this.database.feed_messages.put(nextRow);
                upsertedRow = nextRow;
                if (event.type === 'message.created') {
                  liveTailPreviousSortOrder = await this.extendLiveTail(accountId, conversationId, workspaceId, event.message.sortOrder);
                }
                mirrorMessages = await this.hydrateMessageRows([nextRow]);
              }
            }

            syncCursor = await this.advanceObservedCursor(conversationId, workspaceId, event.cursor);
            mirrorSegments = await this.readConversationSegments(accountId, conversationId);
            metaRevision = await this.bumpMetaRevision(conversationId, workspaceId);
          },
        );
        return true;
      });
      if (!persisted || revoked || metaRevision === undefined) return;

      if (event.message && event.entities) {
        this.bumpEntityRevisions(accountId, [event.message], event.entities);
      }
      this.mirror.applyCommittedSegments(accountId, conversationId, mirrorSegments, syncCursor, metaRevision);
      this.mirror.dropRows(accountId, conversationId, removedIds);
      const coverage =
        event.type === 'message.created' && event.message && liveTailPreviousSortOrder
          ? warmCoverageRange(liveTailPreviousSortOrder, event.message.sortOrder)
          : [];
      this.mirror.addRows(accountId, conversationId, mirrorMessages, emptyEntityBundle(), coverage);
      await this.notifyMessageRowsChanged({
        upserts: upsertedRow ? [upsertedRow] : [],
        deletes: [],
        confirmedDeletes: removedRow ? [removedRow] : [],
      });
    });
  }

  /** Applies sync events in order and then stores the final durable cursor. */
  async applySyncEvents(accountId: string, conversationId: ChannelId, events: readonly SyncEvent[], nextCursor: SyncCursor): Promise<void> {
    const membershipGeneration = this.membershipGeneration(accountId);
    for (const event of events) {
      await this.persistEvent(accountId, conversationId, event, membershipGeneration);
    }
    await this.writeQueue.enqueue(accountId, conversationId, async () => {
      await this.write(async () => {
        const workspaceId = await this.resolveWorkspaceId(conversationId, []);
        await this.database.transaction('rw', [this.database.message_feed_meta], async () => {
          await this.advanceSyncCursor(conversationId, workspaceId, nextCursor);
          await this.bumpMetaRevision(conversationId, workspaceId);
        });
        return true;
      });
    });
  }

  /** Marks all coverage for a conversation stale after sync overflow. */
  async markConversationStale(accountId: string, conversationId: ChannelId, membershipGeneration: number): Promise<void> {
    await this.writeQueue.enqueue(accountId, conversationId, async () => {
      let mirrorSegments: FeedSegment[] | undefined;
      let metaRevision: number | undefined;
      let revoked = false;
      let syncCursor: SyncCursor = '';
      const persisted = await this.write(async () => {
        await this.database.transaction(
          'rw',
          [this.database.channels, this.database.feed_coverage, this.database.feed_messages, this.database.message_feed_meta],
          async () => {
            if (this.isStaleMembership(accountId, membershipGeneration)) {
              revoked = true;
              return;
            }
            const workspaceId = await this.resolveWorkspaceId(conversationId, []);
            const segments = await this.readConversationSegments(accountId, conversationId);
            syncCursor = (await this.readFeedMeta(conversationId))?.sync_cursor ?? '';
            mirrorSegments = segments.map(markSegmentStale);
            await this.replaceConversationSegments(conversationId, mirrorSegments, workspaceId);
            metaRevision = await this.bumpMetaRevision(conversationId, workspaceId);
          },
        );
        return true;
      });
      if (!persisted || revoked || !mirrorSegments || metaRevision === undefined) return;
      this.mirror.applyCommittedSegments(accountId, conversationId, mirrorSegments, syncCursor, metaRevision);
    });
  }

  /**
   * Removes durable rows outside the policy while preserving protected active ranges.
   *
   * No production caller yet. Before wiring one up: retained-row UI treats a watched
   * feed_messages row disappearing as a deletion (useRetainedFeedMessage) — a caller
   * must protect rows retained by open modals/edit state, or deletion signaling must
   * first move to explicit events from the message.deleted write paths.
   */
  async prune(accountId: string, conversationId: ChannelId, policy: PrunePolicy): Promise<void> {
    const maxMessages = normalizedLimit(policy.maxMessages);
    await this.writeQueue.enqueue(accountId, conversationId, async () => {
      let mirrorSegments: FeedSegment[] | undefined;
      let removedIds: MessageId[] = [];
      let metaRevision: number | undefined;
      let syncCursor: SyncCursor = '';
      const persisted = await this.write(async () => {
        await this.database.transaction(
          'rw',
          [this.database.channels, this.database.feed_messages, this.database.feed_coverage, this.database.message_feed_meta],
          async () => {
            const workspaceId = await this.resolveWorkspaceId(conversationId, []);
            const messages = await this.readConversationMessages(conversationId);
            const keepIds = pruneKeepSet(messages, maxMessages, policy.protectedRange);
            const removed = messages.filter((message) => !keepIds.has(message.id));
            if (removed.length === 0) return;

            const survivingSortOrders = messages.filter((message) => keepIds.has(message.id)).map((message) => message.sortOrder);
            const removedSortOrders = removed.map((message) => message.sortOrder);
            const segments = await this.readConversationSegments(accountId, conversationId);
            const nextSegments = coalesceOverlappingSegments(
              segments.flatMap((segment) => resegmentAfterPrune(segment, survivingSortOrders, removedSortOrders)),
            );

            await this.database.feed_messages.bulkDelete(removed.map((message) => message.id));
            await this.replaceConversationSegments(conversationId, nextSegments, workspaceId);
            await this.updateLiveTailMeta(conversationId, workspaceId, nextSegments);

            syncCursor = (await this.readFeedMeta(conversationId))?.sync_cursor ?? '';
            removedIds = removed.map((message) => message.id);
            mirrorSegments = nextSegments;
            metaRevision = await this.bumpMetaRevision(conversationId, workspaceId);
          },
        );
        return true;
      });
      if (!persisted || !mirrorSegments || metaRevision === undefined) return;
      this.mirror.dropRows(accountId, conversationId, removedIds);
      this.mirror.applyCommittedSegments(accountId, conversationId, mirrorSegments, syncCursor, metaRevision);
    });
  }

  /** Clears all feed-owned durable rows and resident mirrors for an account. */
  async purgeAccount(accountId: string): Promise<void> {
    await this.writeQueue.enqueueGlobal(async () => {
      await this.cleanupWrite('account purge', async () => {
        await this.database.transaction(
          'rw',
          [this.database.feed_messages, this.database.feed_coverage, this.database.message_feed_meta],
          async () => {
            await this.database.feed_messages.clear();
            await this.database.feed_coverage.clear();
            await this.database.message_feed_meta.clear();
          },
        );
      });
      this.dropAccountCaches(accountId);
    });
  }

  /** Removes one workspace's feed-owned rows and invalidates resident mirror state. */
  async purgeWorkspace(workspaceId: WorkspaceId): Promise<void> {
    await this.writeQueue.enqueueGlobal(async () => {
      await this.cleanupWrite('workspace purge', async () => {
        await this.database.transaction(
          'rw',
          [this.database.channels, this.database.feed_messages, this.database.feed_coverage, this.database.message_feed_meta],
          async () => {
            const channels = await this.database.channels.where('workspace_id').equals(workspaceId).toArray();
            for (const channel of channels) {
              if (typeof channel.id !== 'string') continue;
              await this.database.feed_messages
                .where('[channel_id+sort_order]')
                .between([channel.id, Dexie.minKey], [channel.id, Dexie.maxKey])
                .delete();
              await this.database.feed_coverage.where('channel_id').equals(channel.id).delete();
              await this.database.message_feed_meta.filter((row) => row.channel_id === channel.id).delete();
            }
            // Filtered deletes catch rows whose channel row is already gone; the channel
            // pass also catches metadata stamped with an unresolved workspace.
            await this.database.feed_messages.filter((row) => row.workspace_id === workspaceId).delete();
            await this.database.feed_coverage.filter((row) => row.workspace_id === workspaceId).delete();
            await this.database.message_feed_meta.filter((row) => row.workspace_id === workspaceId).delete();
          },
        );
      });

      // FeedMirror cannot evict by workspace, so conservatively invalidate all resident
      // conversations while leaving other workspaces' durable rows intact.
      this.mirror.clear();
      this.reportedMissingProfileAuthors.clear();
      for (const key of this.workspaceEntityRevisions.keys()) {
        if (key.endsWith(`:${workspaceId}`)) this.workspaceEntityRevisions.delete(key);
      }
    });
  }

  /** Bumps account membership generation, then purges durable and resident state. */
  async revokeAccount(accountId: string): Promise<void> {
    this.membershipGenerations.set(accountId, this.membershipGeneration(accountId) + 1);
    await this.purgeAccount(accountId);
  }

  /** Clears all feed-owned durable tables and all resident mirrors. */
  async clear(): Promise<void> {
    await this.writeQueue.enqueueGlobal(async () => {
      await this.cleanupWrite('clear', async () => {
        await this.database.transaction(
          'rw',
          [this.database.feed_messages, this.database.feed_coverage, this.database.message_feed_meta],
          async () => {
            await this.database.feed_messages.clear();
            await this.database.feed_coverage.clear();
            await this.database.message_feed_meta.clear();
          },
        );
      });
      this.mirror.clear();
      this.workspaceEntityRevisions.clear();
      this.membershipGenerations.clear();
    });
  }

  private async read<T>(task: () => Promise<T>, fallback: () => T): Promise<T> {
    if (!this.healthy) return fallback();
    try {
      return await task();
    } catch (error: unknown) {
      if (!isDurableBufferFailure(error)) throw error;
      this.markUnhealthy();
      return fallback();
    }
  }

  private async write<T>(task: () => Promise<T>): Promise<T | undefined> {
    if (!this.healthy) return undefined;
    try {
      if (this.simulateWriteFailure) throw simulatedStorageFailure();
      return await task();
    } catch (error: unknown) {
      if (!isDurableBufferFailure(error)) throw error;
      this.markUnhealthy();
      return undefined;
    }
  }

  private async cleanupWrite<T>(operation: string, task: () => Promise<T>): Promise<T> {
    try {
      if (this.simulateWriteFailure) throw simulatedStorageFailure();
      return await task();
    } catch (error: unknown) {
      this.markUnhealthy();
      throw new EchoDexieCleanupError(operation, error);
    }
  }

  private markUnhealthy(): void {
    const changed = this.healthy;
    this.healthy = false;
    this.mirror.clear();
    if (changed) this.onHealthChange?.(false);
  }

  private async readFeedMeta(conversationId: ChannelId): Promise<EchoFeedMetaRow | undefined> {
    const rows = await this.database.message_feed_meta.toArray();
    return (
      rows.find((row) => row.channel_id === conversationId && row.workspace_id !== UNKNOWN_WORKSPACE_ID) ??
      rows.find((row) => row.channel_id === conversationId)
    );
  }

  private async putFeedMeta(meta: EchoFeedMetaRow, previousWorkspaceId?: WorkspaceId): Promise<void> {
    if (previousWorkspaceId && previousWorkspaceId !== meta.workspace_id) {
      await this.database.message_feed_meta.delete([previousWorkspaceId, meta.channel_id]);
    }
    await this.database.message_feed_meta.put(meta);
  }

  private async advanceSyncCursor(conversationId: ChannelId, workspaceId: WorkspaceId, candidate: SyncCursor): Promise<SyncCursor> {
    const meta = await this.readFeedMeta(conversationId);
    const syncCursor = maxSyncCursor(verifiedSyncCursor(meta) ?? '', candidate);
    await this.putFeedMeta(
      {
        workspace_id: feedMetaWorkspaceId(meta, workspaceId),
        channel_id: conversationId,
        ...(syncCursor === '' ? {} : { sync_cursor: maxSyncCursor(meta?.sync_cursor ?? '', syncCursor), verified_sync_cursor: syncCursor }),
        ...(meta?.live_tail_id === undefined ? {} : { live_tail_id: meta.live_tail_id }),
        ...(meta?.live_tail_sort_order === undefined ? {} : { live_tail_sort_order: meta.live_tail_sort_order }),
        meta_revision: meta?.meta_revision ?? 0,
        updated_at: Date.now(),
      },
      meta?.workspace_id,
    );
    return syncCursor;
  }

  // Local page replay needs observed provenance, independent of the verified
  // journal checkpoint: replaying an older live event over a newer page can rewind UI.
  private async advanceObservedCursor(conversationId: ChannelId, workspaceId: WorkspaceId, candidate: SyncCursor): Promise<SyncCursor> {
    const meta = await this.readFeedMeta(conversationId);
    const cursor = maxSyncCursor(meta?.sync_cursor ?? '', candidate);
    const checkpoint = feedSyncCheckpoint(meta);
    const bootstrapCursor = checkpoint.kind === 'cold' ? candidate
      : checkpoint.kind === 'bootstrap' ? earlierSyncCursor(checkpoint.cursor, candidate)
      : undefined;
    await this.putFeedMeta({
      ...meta,
      workspace_id: feedMetaWorkspaceId(meta, workspaceId),
      channel_id: conversationId,
      sync_cursor: cursor,
      ...(bootstrapCursor === undefined ? {} : { bootstrap_sync_cursor: bootstrapCursor }),
      updated_at: Date.now(),
    }, meta?.workspace_id);
    return cursor;
  }

  private async bumpMetaRevision(conversationId: ChannelId, workspaceId: WorkspaceId): Promise<number> {
    const meta = await this.readFeedMeta(conversationId);
    const metaRevision = (meta?.meta_revision ?? 0) + 1;
    await this.putFeedMeta(
      {
        workspace_id: feedMetaWorkspaceId(meta, workspaceId),
        channel_id: conversationId,
        ...(meta?.sync_cursor === undefined ? {} : { sync_cursor: meta.sync_cursor }),
        ...(meta?.verified_sync_cursor === undefined ? {} : { verified_sync_cursor: meta.verified_sync_cursor }),
        ...(meta?.bootstrap_sync_cursor === undefined ? {} : { bootstrap_sync_cursor: meta.bootstrap_sync_cursor }),
        ...(meta?.live_tail_id === undefined ? {} : { live_tail_id: meta.live_tail_id }),
        ...(meta?.live_tail_sort_order === undefined ? {} : { live_tail_sort_order: meta.live_tail_sort_order }),
        meta_revision: metaRevision,
        updated_at: Date.now(),
      },
      meta?.workspace_id,
    );
    return metaRevision;
  }

  private async updateLiveTailMeta(conversationId: ChannelId, workspaceId: WorkspaceId, segments: readonly FeedSegment[]): Promise<void> {
    const liveTail = segments.find((segment) => segment.reachesLiveTail);
    if (!liveTail) return;
    const newest = await this.readNewestMessageInRange(conversationId, liveTail.oldestSortOrder, liveTail.newestSortOrder);
    if (!newest) return;

    const meta = await this.readFeedMeta(conversationId);
    await this.putFeedMeta(
      {
        workspace_id: feedMetaWorkspaceId(meta, workspaceId),
        channel_id: conversationId,
        ...(meta?.sync_cursor === undefined ? {} : { sync_cursor: meta.sync_cursor }),
        ...(meta?.verified_sync_cursor === undefined ? {} : { verified_sync_cursor: meta.verified_sync_cursor }),
        ...(meta?.bootstrap_sync_cursor === undefined ? {} : { bootstrap_sync_cursor: meta.bootstrap_sync_cursor }),
        live_tail_id: newest.id,
        live_tail_sort_order: newest.sortOrder,
        meta_revision: meta?.meta_revision ?? 0,
        updated_at: Date.now(),
      },
      meta?.workspace_id,
    );
  }

  private async extendLiveTail(
    accountId: string,
    conversationId: ChannelId,
    workspaceId: WorkspaceId,
    sortOrder: SortOrder,
  ): Promise<SortOrder | undefined> {
    const segments = await this.readConversationSegments(accountId, conversationId);
    const tail = segments.find((segment) => segment.reachesLiveTail);
    if (!tail || compareSortOrders(sortOrder, tail.newestSortOrder) <= 0) return undefined;

    const nextTail = {
      ...tail,
      newestSortOrder: sortOrder,
      newerCursor: undefined,
    };
    const nextSegments = segments.map((segment) => (segment.id === tail.id ? nextTail : segment));
    await this.replaceConversationSegments(conversationId, nextSegments, workspaceId);
    await this.updateLiveTailMeta(conversationId, workspaceId, nextSegments);
    return tail.newestSortOrder;
  }

  private async putPageMessages(messages: readonly WireMessage[], syncCursor: SyncCursor): Promise<EchoMessageRow[]> {
    const existingRows = await this.database.feed_messages.bulkGet(messages.map((message) => message.id));
    const rowsToWrite = messages
      .filter((message, index) => {
        const existing = existingRows[index];
        return !existing || !isExistingRowFresher(existing, message, syncCursor);
      })
      .map((message) => storedEchoMessage(message, syncCursor));
    if (rowsToWrite.length > 0) {
      await this.database.feed_messages.bulkPut(rowsToWrite);
    }
    return rowsToWrite;
  }

  private async reconcileAbsentMessages(
    conversationId: ChannelId,
    pageSegment: FeedSegment,
    pageMessages: readonly WireMessage[],
    pageCursor: SyncCursor,
  ): Promise<EchoMessageRow[]> {
    const returnedIds = new Set(pageMessages.map((message) => message.id));
    const storedInRange = await this.readRawRowsInRange(conversationId, pageSegment.oldestSortOrder, pageSegment.newestSortOrder);
    const removed = storedInRange.filter((row) => !returnedIds.has(row.id) && !isExistingRowFresherThanCursor(row, pageCursor));
    if (removed.length > 0) {
      await this.database.feed_messages.bulkDelete(removed.map((row) => row.id));
    }
    return removed;
  }

  private async notifyMessageRowsChanged(changes: {
    readonly upserts: readonly EchoMessageRow[];
    readonly deletes: readonly EchoMessageRow[];
    readonly confirmedDeletes: readonly EchoMessageRow[];
  }): Promise<void> {
    if (
      !this.onMessageRowsChanged ||
      (changes.upserts.length === 0 && changes.deletes.length === 0 && changes.confirmedDeletes.length === 0)
    ) {
      return;
    }

    try {
      await this.onMessageRowsChanged(changes);
    } catch (error) {
      logError('echo-dexie-buffer', 'Message projection update failed after the feed transaction committed', error);
    }
  }

  private async normalizeSegments(segments: readonly FeedSegment[]): Promise<FeedSegment[]> {
    const normalized: FeedSegment[] = [];
    for (const segment of segments) {
      const next = await this.normalizeSegment(segment);
      if (next) normalized.push(next);
    }
    return coalesceOverlappingSegments(normalized);
  }

  private async normalizeSegment(segment: FeedSegment): Promise<FeedSegment | undefined> {
    const oldest = await this.readOldestMessageInRange(segment.conversationId, segment.oldestSortOrder, segment.newestSortOrder);
    if (!oldest) return undefined;

    const newest = await this.readNewestMessageInRange(segment.conversationId, segment.oldestSortOrder, segment.newestSortOrder);
    if (!newest) return undefined;

    return {
      id: segmentId(segment.accountId, segment.conversationId, oldest.sortOrder, newest.sortOrder),
      accountId: segment.accountId,
      conversationId: segment.conversationId,
      oldestSortOrder: oldest.sortOrder,
      newestSortOrder: newest.sortOrder,
      reachesStart: segment.reachesStart,
      reachesLiveTail: segment.reachesLiveTail,
      ...(segment.reachesStart ? {} : { olderCursor: oldest.sortOrder }),
      ...(segment.reachesLiveTail ? {} : { newerCursor: newest.sortOrder }),
      ...(segment.stale ? { stale: true } : {}),
    };
  }

  private async readLatestMessages(conversationId: ChannelId, count: number): Promise<EchoWireMessage[]> {
    if (count === 0) return [];
    const newest = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .reverse()
      .limit(count)
      .toArray();
    return this.hydrateMessageRows(newest.reverse());
  }

  private async readConversationMessages(conversationId: ChannelId): Promise<EchoWireMessage[]> {
    const rows = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .toArray();
    return parseRows(rows);
  }

  private async readMessagesBefore(
    conversationId: ChannelId,
    segment: FeedSegment,
    boundary: SortOrder,
    count: number,
  ): Promise<EchoWireMessage[]> {
    if (count === 0) return [];
    const older = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, segment.oldestSortOrder], [conversationId, boundary], true, false)
      .reverse()
      .limit(count)
      .toArray();
    return this.hydrateMessageRows(older.reverse());
  }

  private async readMessagesAfter(
    conversationId: ChannelId,
    segment: FeedSegment,
    boundary: SortOrder,
    count: number,
  ): Promise<EchoWireMessage[]> {
    if (count === 0) return [];
    const rows = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, boundary], [conversationId, segment.newestSortOrder], false, true)
      .limit(count)
      .toArray();
    return this.hydrateMessageRows(rows);
  }

  private async readMessagesAround(
    conversationId: ChannelId,
    segment: FeedSegment,
    boundary: SortOrder,
    count: number,
  ): Promise<EchoWireMessage[]> {
    if (count === 0) return [];
    const [older, newer] = await Promise.all([
      this.database.feed_messages
        .where('[channel_id+sort_order]')
        .between([conversationId, segment.oldestSortOrder], [conversationId, boundary], true, true)
        .reverse()
        .limit(count)
        .toArray(),
      this.database.feed_messages
        .where('[channel_id+sort_order]')
        .between([conversationId, boundary], [conversationId, segment.newestSortOrder], false, true)
        .limit(count)
        .toArray(),
    ]);
    let olderCount = Math.min(Math.ceil(count / 2), older.length);
    const newerCount = Math.min(count - olderCount, newer.length);
    olderCount = Math.min(older.length, count - newerCount);
    return this.hydrateMessageRows([...older.slice(0, olderCount).reverse(), ...newer.slice(0, newerCount)]);
  }

  private async readMessagesInRange(
    conversationId: ChannelId,
    oldestSortOrder: SortOrder,
    newestSortOrder: SortOrder,
  ): Promise<EchoWireMessage[]> {
    return this.hydrateMessageRows(await this.readRawRowsInRange(conversationId, oldestSortOrder, newestSortOrder));
  }

  private async hydrateMessageRows(rows: readonly EchoMessageRow[]): Promise<EchoWireMessage[]> {
    const channelKeys: [WorkspaceId, string][] = [];
    const emojiKeys: [WorkspaceId, string][] = [];
    const profileKeys: [WorkspaceId, string][] = [];
    const seenChannelKeys = new Set<string>();
    const seenEmojiKeys = new Set<string>();
    const seenProfileKeys = new Set<string>();
    const systemEmojiIds = new Set<string>();

    for (const row of rows) {
      if (row.author_type === 'profile' && typeof row.author_id === 'string') {
        addWorkspaceEntityKey(profileKeys, seenProfileKeys, row.workspace_id, row.author_id);
      }
      for (const reaction of row.reactions) {
        const profileId = recordString(reaction, 'profile_id');
        const emojiId = recordString(reaction, 'emoji_id');
        if (profileId) addWorkspaceEntityKey(profileKeys, seenProfileKeys, row.workspace_id, profileId);
        if (emojiId) {
          addWorkspaceEntityKey(emojiKeys, seenEmojiKeys, row.workspace_id, emojiId);
          systemEmojiIds.add(emojiId);
        }
      }
      const targets = rowEntityTargets(row);
      for (const profileId of targets.profile) {
        addWorkspaceEntityKey(profileKeys, seenProfileKeys, row.workspace_id, profileId);
      }
      for (const channelId of targets.channel) {
        addWorkspaceEntityKey(channelKeys, seenChannelKeys, row.workspace_id, channelId);
      }
      for (const emojiId of targets.emoji) {
        addWorkspaceEntityKey(emojiKeys, seenEmojiKeys, row.workspace_id, emojiId);
        systemEmojiIds.add(emojiId);
      }
    }

    const systemEmojiKeys: [WorkspaceId, string][] = [...systemEmojiIds].map((id) => ['system', id]);
    const [profileRows, channelRows, emojiRows, systemEmojiRows] = await Promise.all([
      profileKeys.length > 0 ? this.database.profiles.bulkGet(profileKeys) : [],
      channelKeys.length > 0 ? this.database.channels.bulkGet(channelKeys) : [],
      emojiKeys.length > 0 ? this.database.emojis.bulkGet(emojiKeys) : [],
      systemEmojiKeys.length > 0 ? this.database.emojis.bulkGet(systemEmojiKeys) : [],
    ]);
    const profiles = recordsByWorkspaceEntityKey(profileKeys, profileRows);
    const channels = recordsByWorkspaceEntityKey(channelKeys, channelRows);
    const emojis = recordsByWorkspaceEntityKey(emojiKeys, emojiRows);
    const systemEmojis = recordsByWorkspaceEntityKey(systemEmojiKeys, systemEmojiRows);

    return parseRows(
      rows.map((row) => {
        const author =
          row.author_type === 'profile' && typeof row.author_id === 'string'
            ? buildProfileSnapshot(profiles.get(workspaceEntityKey(row.workspace_id, row.author_id)))
            : null;
        const missingProfileAuthor = row.author_type === 'profile' && typeof row.author_id === 'string' && !author;
        if (missingProfileAuthor) this.reportMissingProfileAuthor(row);
        const reactions = row.reactions.map((reaction) => {
          const profileId = recordString(reaction, 'profile_id');
          const emojiId = recordString(reaction, 'emoji_id');
          const profile = profileId ? buildProfileSnapshot(profiles.get(workspaceEntityKey(row.workspace_id, profileId))) : null;
          const emoji = emojiId
            ? buildEmojiSnapshot(
                emojis.get(workspaceEntityKey(row.workspace_id, emojiId)) ?? systemEmojis.get(workspaceEntityKey('system', emojiId)),
              )
            : null;
          return {
            ...reaction,
            ...(emoji ? { emoji } : {}),
            ...(profile ? { profile } : {}),
          };
        });
        const targets = rowEntityTargets(row);
        const entitySnapshots = buildHydratedEntitySnapshots(row.workspace_id, targets, profiles, channels, emojis, systemEmojis);
        return {
          ...row,
          ...(author ? { author } : {}),
          ...(missingProfileAuthor ? { flags: placeholderAuthorFlags(row.flags) } : {}),
          reactions,
          ...(Object.keys(entitySnapshots).length > 0 ? { entity_snapshots: entitySnapshots } : {}),
        };
      }),
    );
  }

  private reportMissingProfileAuthor(row: EchoMessageRow): void {
    if (typeof row.author_id !== 'string') return;
    const key = `${row.workspace_id}\x1f${row.channel_id}\x1f${row.id}\x1f${row.author_id}`;
    if (this.reportedMissingProfileAuthors.has(key)) return;
    this.reportedMissingProfileAuthors.add(key);
    logError('message-feed', 'Missing profile author for feed message', {
      workspaceId: row.workspace_id,
      channelId: row.channel_id,
      messageId: row.id,
      authorId: row.author_id,
    });
  }

  private async readRawRowsInRange(
    conversationId: ChannelId,
    oldestSortOrder: SortOrder,
    newestSortOrder: SortOrder,
  ): Promise<EchoMessageRow[]> {
    return this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, oldestSortOrder], [conversationId, newestSortOrder], true, true)
      .toArray();
  }

  private async readOldestMessageInRange(
    conversationId: ChannelId,
    oldestSortOrder: SortOrder,
    newestSortOrder: SortOrder,
  ): Promise<EchoWireMessage | undefined> {
    const row = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, oldestSortOrder], [conversationId, newestSortOrder], true, true)
      .first();
    return row ? parseRow(row) : undefined;
  }

  private async readNewestMessageInRange(
    conversationId: ChannelId,
    oldestSortOrder: SortOrder,
    newestSortOrder: SortOrder,
  ): Promise<EchoWireMessage | undefined> {
    const row = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, oldestSortOrder], [conversationId, newestSortOrder], true, true)
      .reverse()
      .first();
    return row ? parseRow(row) : undefined;
  }

  private async readConversationSegments(accountId: string, conversationId: ChannelId): Promise<FeedSegment[]> {
    const rows = await this.database.feed_coverage.where('channel_id').equals(conversationId).toArray();
    return rows
      .map((row) => feedSegmentFromInterval(accountId, row))
      .sort((left, right) => compareSortOrders(left.oldestSortOrder, right.oldestSortOrder));
  }

  private async replaceConversationSegments(
    conversationId: ChannelId,
    segments: readonly FeedSegment[],
    workspaceId: WorkspaceId,
  ): Promise<void> {
    await this.database.feed_coverage.where('channel_id').equals(conversationId).delete();
    if (segments.length > 0) {
      await this.database.feed_coverage.bulkPut(segments.map((segment) => intervalFromFeedSegment(segment, workspaceId)));
    }
  }

  private async resolveWorkspaceId(conversationId: ChannelId, messages: readonly WireMessage[]): Promise<WorkspaceId> {
    const fromMessage = messages[0]?.workspaceId;
    if (fromMessage && fromMessage !== UNKNOWN_WORKSPACE_ID) return fromMessage;
    const meta = await this.readFeedMeta(conversationId);
    if (meta?.workspace_id && meta.workspace_id !== UNKNOWN_WORKSPACE_ID) return meta.workspace_id;
    const message = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .first();
    if (message?.workspace_id && message.workspace_id !== UNKNOWN_WORKSPACE_ID) return message.workspace_id;
    const channels = await this.database.channels.toArray();
    const channel = channels.find((row) => row.id === conversationId);
    if (typeof channel?.workspace_id === 'string' && channel.workspace_id.length > 0) {
      return channel.workspace_id;
    }
    return UNKNOWN_WORKSPACE_ID;
  }

  private async readConversationWorkspaceIds(conversationId: ChannelId): Promise<WorkspaceId[]> {
    const rows = await this.database.feed_messages
      .where('[channel_id+sort_order]')
      .between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey])
      .toArray();
    return [...new Set(rows.map((row) => row.workspace_id))];
  }

  private bumpEntityRevisions(accountId: string, messages: readonly WireMessage[], entities: EntityBundle): void {
    this.bumpEntityRevisionsForWorkspaceIds(accountId, workspaceIdsForMessages(messages), entities);
  }

  private bumpEntityRevisionsForWorkspaceIds(accountId: string, workspaceIds: readonly WorkspaceId[], entities: EntityBundle): void {
    if (entities.profiles.length === 0 && entities.channels.length === 0 && entities.emoji.length === 0) return;
    for (const workspaceId of workspaceIds) {
      const key = workspaceEntityRevisionKey(accountId, workspaceId);
      this.workspaceEntityRevisions.set(key, (this.workspaceEntityRevisions.get(key) ?? 0) + 1);
    }
  }

  private isStaleMembership(accountId: string, generation: number): boolean {
    return generation < this.membershipGeneration(accountId);
  }

  private dropAccountCaches(accountId: string): void {
    this.mirror.dropAccount(accountId);
    for (const key of this.workspaceEntityRevisions.keys()) {
      if (key.startsWith(`${accountId}:`)) this.workspaceEntityRevisions.delete(key);
    }
  }
}

function parseRows(rows: readonly EchoMessageRow[]): EchoWireMessage[] {
  return rows.map(parseRow);
}

function feedMetaWorkspaceId(meta: EchoFeedMetaRow | undefined, resolvedWorkspaceId: WorkspaceId): WorkspaceId {
  if (meta?.workspace_id && meta.workspace_id !== UNKNOWN_WORKSPACE_ID) return meta.workspace_id;
  return resolvedWorkspaceId;
}

class StoredEchoMessageParseError extends Error {
  constructor(rowId: MessageId, path: string, parseMessage: string) {
    super(`Stored echo message ${rowId} failed feed parsing at ${path}: ${parseMessage}`);
    this.name = 'StoredEchoMessageParseError';
  }
}

function parseRow(row: EchoMessageRow): EchoWireMessage {
  const parsed = parseEchoWireMessage(publicMessageRow(row));
  if (!parsed.ok) {
    throw new StoredEchoMessageParseError(row.id, parsed.error.path, parsed.error.message);
  }
  return parsed.value;
}

function publicMessageRow(row: EchoMessageRow): Record<string, unknown> {
  const { _feed_mutation_cursor: _feedMutationCursor, ...publicRow } = row;
  return publicRow;
}

function isDurableBufferFailure(error: unknown): boolean {
  return isStorageFailure(error) || isStoredEchoMessageParseFailure(error);
}

function isStoredEchoMessageParseFailure(error: unknown, seen = new Set<unknown>()): boolean {
  if (!error || seen.has(error)) return false;
  seen.add(error);

  if (error instanceof StoredEchoMessageParseError) return true;

  if (typeof error === 'object') {
    // SAFETY: Error wrappers from Dexie/DOMException-like failures use optional cause/inner links.
    const wrapped = error as { readonly cause?: unknown; readonly inner?: unknown };
    return isStoredEchoMessageParseFailure(wrapped.inner, seen) || isStoredEchoMessageParseFailure(wrapped.cause, seen);
  }

  return false;
}

function storedEchoMessage(message: WireMessage, syncCursor: SyncCursor): EchoMessageRow {
  // SAFETY: Runtime Echo messages carry these fields; the base fallback supports generic FeedSession fixtures.
  const echo = message as EchoWireMessage;
  const hasEchoAuthorFields = Object.prototype.hasOwnProperty.call(message, 'rawAuthorId');
  return {
    id: message.id,
    workspace_id: message.workspaceId,
    channel_id: message.channelId,
    text: message.bodyHtml ?? message.body,
    sort_order: message.sortOrder,
    timestamp: isoToUnixSeconds(message.createdAt),
    author_id: hasEchoAuthorFields ? echo.rawAuthorId : (message.authorId ?? null),
    author_type: hasEchoAuthorFields ? echo.authorType : 'profile',
    ...(message.flags === undefined ? {} : { flags: [...message.flags] }),
    // SAFETY: Echo attachments/reactions/previews preserve serializer-only fields through the base types.
    attachments: (message.attachments as unknown as readonly EchoWireAttachment[]).map(storedAttachment),
    reactions: (message.reactions as unknown as readonly EchoWireReaction[]).map((reaction) => storedReaction(reaction, message)),
    mentions: (echo.mentions ?? []).map(storedMention),
    link_previews: echo.linkPreviews
      ? echo.linkPreviews.map(storedLinkPreview)
      : // SAFETY: Base WireMessage previews are Echo chat previews before storage normalization.
        (message.previews as unknown as readonly EchoChatPreview[]).map(storedChatPreview),
    contains_only_attachments: message.containsOnlyAttachments ?? false,
    contains_only_entities: message.containsOnlyEntities ?? false,
    contains_only_emoji_entities: message.containsOnlyEmojiEntities ?? false,
    ...(echo.type === undefined ? {} : { type: echo.type }),
    ...(echo.data === undefined ? {} : { data: echo.data }),
    ...(echo.actors === undefined ? {} : { actors: echo.actors }),
    ...(echo.sourceWidgetId === undefined ? {} : { source_widget_id: echo.sourceWidgetId }),
    ...(echo.sourceWidget === undefined ? {} : { source_widget: echo.sourceWidget }),
    ...(echo.clientRequestId === undefined ? {} : { optimistic_sort_order: echo.clientRequestId }),
    ...(echo.replyToMessageId === undefined ? {} : { reply_to_message_id: echo.replyToMessageId }),
    ...(message.freshSince === undefined ? {} : { fresh_since: message.freshSince }),
    _feed_mutation_cursor: syncCursor,
  };
}

function storedAttachment(attachment: EchoWireAttachment | WireAttachment): Record<string, unknown> {
  // SAFETY: Base WireAttachment omits Echo serializer fields; absent fields are handled below.
  const echo = attachment as EchoWireAttachment;
  return {
    id: attachment.id,
    ...(echo.workspaceId === undefined ? {} : { workspace_id: echo.workspaceId }),
    metadata: {
      file_name: attachment.fileName,
      content_type: attachment.contentType,
      content_length: attachment.sizeBytes,
      ...(attachment.thumbhash === undefined ? {} : { thumbhash: attachment.thumbhash }),
      ...(echo.thumbWidth === undefined || echo.thumbHeight === undefined ? {} : { thumbsize: `${echo.thumbWidth}x${echo.thumbHeight}` }),
      ...(echo.contentMd5 === undefined ? {} : { content_md5: echo.contentMd5 }),
    },
    public_url: attachment.originalUrl,
    variants: storedMediaVariants(attachment.variants),
    created_at: echo.createdAt ?? 0,
    updated_at: echo.updatedAt ?? 0,
  };
}

function storedMediaVariants(variants: WireAttachment['variants']): Record<string, unknown> {
  return {
    ...(variants?.medium === undefined ? {} : { medium: storedMediaVariant(variants.medium) }),
    ...(variants?.large === undefined ? {} : { large: storedMediaVariant(variants.large) }),
  };
}

function storedMediaVariant(variant: WireMediaVariant): Record<string, unknown> {
  return {
    width: variant.width,
    height: variant.height,
    public_url: variant.url,
  };
}

function storedReaction(reaction: EchoWireReaction, message: WireMessage): Record<string, unknown> {
  return {
    id: reaction.id,
    workspace_id: reaction.workspaceId ?? message.workspaceId,
    channel_id: reaction.channelId ?? message.channelId,
    message_id: reaction.messageId ?? message.id,
    profile_id: reaction.profileId,
    emoji_id: reaction.emojiId,
    sort_order: reaction.sortOrder,
    pos_x: reaction.posX ?? null,
    pos_y: reaction.posY ?? null,
    scale: reaction.scale ?? null,
    angle: reaction.angle ?? null,
    ...(reaction.clientRequestId === undefined ? {} : { optimistic_sort_order: reaction.clientRequestId }),
  };
}

function storedMention(mention: EchoWireMention): Record<string, unknown> {
  return {
    id: mention.id,
    workspace_id: mention.workspaceId,
    timestamp: mention.timestamp,
    sender_id: mention.senderId,
    recipient_id: mention.recipientId,
    ...(mention.sourceType === undefined ? {} : { source_type: mention.sourceType }),
    ...(mention.sourceId === undefined ? {} : { source_id: mention.sourceId }),
    ...(mention.mentionableType === undefined ? {} : { mentionable_type: mention.mentionableType }),
    ...(mention.mentionableId === undefined ? {} : { mentionable_id: mention.mentionableId }),
    ...(mention.emailSubject === undefined ? {} : { email_subject: mention.emailSubject }),
    ...(mention.notificationTitle === undefined ? {} : { notification_title: mention.notificationTitle }),
    ...(mention.summary === undefined ? {} : { summary: mention.summary }),
  };
}

function storedLinkPreview(preview: EchoWireLinkPreview): Record<string, unknown> {
  return {
    id: preview.id,
    dismissed: preview.dismissed,
    ...(preview.dismissedAt === undefined ? {} : { dismissed_at: preview.dismissedAt }),
    url: preview.url,
    ...(preview.title === undefined ? {} : { title: preview.title }),
    ...(preview.description === undefined ? {} : { description: preview.description }),
    ...(preview.imageUrl === undefined ? {} : { image_url: preview.imageUrl }),
    ...(preview.imageWidth === undefined ? {} : { image_width: preview.imageWidth }),
    ...(preview.imageHeight === undefined ? {} : { image_height: preview.imageHeight }),
    ...(preview.logoUrl === undefined ? {} : { logo_url: preview.logoUrl }),
    ...(preview.logoWidth === undefined ? {} : { logo_width: preview.logoWidth }),
    ...(preview.logoHeight === undefined ? {} : { logo_height: preview.logoHeight }),
    ...(preview.publisher === undefined ? {} : { publisher: preview.publisher }),
    ...(preview.author === undefined ? {} : { author: preview.author }),
    ...(preview.lang === undefined ? {} : { lang: preview.lang }),
    ...(preview.canonicalUrl === undefined ? {} : { canonical_url: preview.canonicalUrl }),
    ...(preview.date === undefined ? {} : { date: preview.date }),
    ...(preview.datePublished === undefined ? {} : { date_published: preview.datePublished }),
    ...(preview.dateModified === undefined ? {} : { date_modified: preview.dateModified }),
  };
}

function storedChatPreview(preview: EchoChatPreview): Record<string, unknown> {
  return storedLinkPreview({
    id: preview.id,
    dismissed: preview.dismissed,
    ...(preview.dismissedAt === undefined ? {} : { dismissedAt: preview.dismissedAt }),
    url: preview.url,
    title: preview.title,
    description: preview.description,
    ...(preview.imageUrl === undefined ? {} : { imageUrl: preview.imageUrl }),
    ...(preview.imageWidth === undefined ? {} : { imageWidth: preview.imageWidth }),
    ...(preview.imageHeight === undefined ? {} : { imageHeight: preview.imageHeight }),
    ...(preview.logoUrl === undefined ? {} : { logoUrl: preview.logoUrl }),
    ...(preview.logoWidth === undefined ? {} : { logoWidth: preview.logoWidth }),
    ...(preview.logoHeight === undefined ? {} : { logoHeight: preview.logoHeight }),
    ...(preview.publisher === undefined ? {} : { publisher: preview.publisher }),
    ...(preview.author === undefined ? {} : { author: preview.author }),
    ...(preview.lang === undefined ? {} : { lang: preview.lang }),
    ...(preview.canonicalUrl === undefined ? {} : { canonicalUrl: preview.canonicalUrl }),
    ...(preview.date === undefined ? {} : { date: preview.date }),
    ...(preview.datePublished === undefined ? {} : { datePublished: preview.datePublished }),
    ...(preview.dateModified === undefined ? {} : { dateModified: preview.dateModified }),
  });
}

function feedSegmentFromInterval(accountId: string, row: EchoCoverageIntervalRow): FeedSegment {
  return {
    id: segmentId(accountId, row.channel_id, row.lower_sort_order, row.upper_sort_order),
    accountId,
    conversationId: row.channel_id,
    oldestSortOrder: row.lower_sort_order,
    newestSortOrder: row.upper_sort_order,
    reachesStart: row.reaches_start ?? row.reaches_oldest ?? false,
    reachesLiveTail: row.reaches_live_tail ?? row.reaches_latest ?? false,
    ...((row.reaches_start ?? row.reaches_oldest ?? false) ? {} : { olderCursor: row.lower_sort_order }),
    ...((row.reaches_live_tail ?? row.reaches_latest ?? false) ? {} : { newerCursor: row.upper_sort_order }),
    ...(row.stale ? { stale: true } : {}),
  };
}

function intervalFromFeedSegment(segment: FeedSegment, workspaceId: WorkspaceId): EchoCoverageIntervalRow {
  return {
    id: `${segment.conversationId}:${segment.oldestSortOrder}:${segment.newestSortOrder}`,
    workspace_id: workspaceId,
    channel_id: segment.conversationId,
    lower_sort_order: segment.oldestSortOrder,
    upper_sort_order: segment.newestSortOrder,
    reaches_start: segment.reachesStart,
    reaches_live_tail: segment.reachesLiveTail,
    reaches_oldest: segment.reachesStart,
    reaches_latest: segment.reachesLiveTail,
    ...(segment.stale ? { stale: true } : {}),
    fetched_at: Date.now(),
  };
}

function isExistingRowFresher(existing: EchoMessageRow, incoming: WireMessage, incomingCursor: SyncCursor): boolean {
  if (isExistingRowFresherThanCursor(existing, incomingCursor)) return true;
  return existingFreshSince(existing) > incomingFreshSince(incoming);
}

function isExistingRowFresherThanCursor(existing: EchoMessageRow, incomingCursor: SyncCursor): boolean {
  const existingCursor = existing._feed_mutation_cursor;
  return existingCursor !== undefined && compareSyncCursors(existingCursor, incomingCursor) > 0;
}

function existingFreshSince(existing: EchoMessageRow): number {
  return typeof existing.fresh_since === 'number' && Number.isFinite(existing.fresh_since) ? existing.fresh_since : -Infinity;
}

function incomingFreshSince(incoming: WireMessage): number {
  return typeof incoming.freshSince === 'number' && Number.isFinite(incoming.freshSince) ? incoming.freshSince : -Infinity;
}

function isoToUnixSeconds(value: string): number {
  const milliseconds = Date.parse(value);
  if (!Number.isFinite(milliseconds)) return 0;
  return Math.floor(milliseconds / 1000);
}

function emptyEntityBundle(): EntityBundle {
  return {
    profiles: [],
    channels: [],
    emoji: [],
  };
}

function emptyReadHydration(result?: LocalPageResult): EchoReadHydration {
  return {
    coverage: [],
    entities: emptyEntityBundle(),
    messages: [],
    ...(result === undefined ? {} : { result }),
    segments: [],
    syncCursor: '',
  };
}

function normalizedLimit(limit: number): number {
  return Math.max(0, Math.floor(limit));
}

function pruneKeepSet(
  messages: readonly { readonly id: MessageId; readonly sortOrder: SortOrder }[],
  maxMessages: number,
  protectedRange: PrunePolicy['protectedRange'],
): ReadonlySet<MessageId> {
  const keepIds = new Set<MessageId>();
  const newestStart = Math.max(0, messages.length - maxMessages);

  for (const message of messages.slice(newestStart)) {
    keepIds.add(message.id);
  }
  for (const message of messages) {
    if (isProtectedSortOrder(protectedRange, message.sortOrder)) {
      keepIds.add(message.id);
    }
  }

  return keepIds;
}

function isProtectedSortOrder(protectedRange: PrunePolicy['protectedRange'], sortOrder: SortOrder): boolean {
  if (!protectedRange) return false;
  return (
    compareSortOrders(protectedRange.oldestSortOrder, protectedRange.newestSortOrder) <= 0 &&
    compareSortOrders(protectedRange.oldestSortOrder, sortOrder) <= 0 &&
    compareSortOrders(sortOrder, protectedRange.newestSortOrder) <= 0
  );
}

function workspaceIdsForMessages(messages: readonly WireMessage[]): WorkspaceId[] {
  return [...new Set(messages.map((message) => message.workspaceId))];
}

function workspaceEntityRevisionKey(accountId: string, workspaceId: WorkspaceId): string {
  return `${accountId}:${workspaceId}`;
}

function workspaceEntityKey(workspaceId: WorkspaceId, entityId: string): string {
  return `${workspaceId}\x1f${entityId}`;
}

function addWorkspaceEntityKey(keys: [WorkspaceId, string][], seen: Set<string>, workspaceId: WorkspaceId, entityId: string): void {
  const key = workspaceEntityKey(workspaceId, entityId);
  if (seen.has(key)) return;
  seen.add(key);
  keys.push([workspaceId, entityId]);
}

function recordsByWorkspaceEntityKey(
  keys: readonly [WorkspaceId, string][],
  rows: readonly (Record<string, unknown> | undefined)[],
): Map<string, Record<string, unknown>> {
  const records = new Map<string, Record<string, unknown>>();
  keys.forEach(([workspaceId, entityId], index) => {
    const row = rows[index];
    if (row) records.set(workspaceEntityKey(workspaceId, entityId), row);
  });
  return records;
}

function recordString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function placeholderAuthorFlags(flags: readonly string[] | undefined): string[] {
  return [...new Set([...(flags ?? []), ECHO_PLACEHOLDER_AUTHOR_FLAG])];
}

function rowEntityTargets(row: EchoMessageRow): EntityTargets {
  const textTargets = extractEntityTargets(row.text);
  const dataTargets = entityTargetsFromMessageData(row.data);
  if (dataTargets.profile.length === 0 && dataTargets.channel.length === 0) return textTargets;
  return {
    channel: [...new Set([...textTargets.channel, ...dataTargets.channel])],
    emoji: textTargets.emoji,
    profile: [...new Set([...textTargets.profile, ...dataTargets.profile])],
    spammer: textTargets.spammer,
  };
}

function buildHydratedEntitySnapshots(
  workspaceId: WorkspaceId,
  targets: EntityTargets,
  profiles: ReadonlyMap<string, Record<string, unknown>>,
  channels: ReadonlyMap<string, Record<string, unknown>>,
  emojis: ReadonlyMap<string, Record<string, unknown>>,
  systemEmojis: ReadonlyMap<string, Record<string, unknown>>,
): Record<string, Record<string, Record<string, unknown> | null>> {
  const result: Record<string, Record<string, Record<string, unknown> | null>> = {};
  if (targets.profile.length > 0) {
    result.profile = Object.fromEntries(
      targets.profile.map((id) => [id, buildProfileSnapshot(profiles.get(workspaceEntityKey(workspaceId, id)))]),
    );
  }
  if (targets.channel.length > 0) {
    result.channel = Object.fromEntries(
      targets.channel.map((id) => [id, buildChannelSnapshot(channels.get(workspaceEntityKey(workspaceId, id)))]),
    );
  }
  if (targets.emoji.length > 0) {
    result.emoji = Object.fromEntries(
      targets.emoji.map((id) => [
        id,
        buildEmojiSnapshot(emojis.get(workspaceEntityKey(workspaceId, id)) ?? systemEmojis.get(workspaceEntityKey('system', id))),
      ]),
    );
  }
  return result;
}

function simulatedStorageFailure(): Error {
  const error = new Error('Simulated echo Dexie write failure');
  error.name = 'AbortError';
  return error;
}

type FeedSyncCheckpoint =
  | { readonly kind: 'cold' }
  | { readonly kind: 'bootstrap'; readonly cursor: SyncCursor }
  | { readonly kind: 'legacy' }
  | { readonly kind: 'verified'; readonly cursor: SyncCursor };

function feedSyncCheckpoint(meta: EchoFeedMetaRow | undefined): FeedSyncCheckpoint {
  if (!meta) return { kind: 'cold' };
  if (meta.verified_sync_cursor !== undefined) return { kind: 'verified', cursor: meta.verified_sync_cursor };
  if (meta.bootstrap_sync_cursor !== undefined) return { kind: 'bootstrap', cursor: meta.bootstrap_sync_cursor };
  return { kind: 'legacy' };
}

function verifiedSyncCursor(meta: EchoFeedMetaRow | undefined): SyncCursor | undefined {
  const checkpoint = feedSyncCheckpoint(meta);
  switch (checkpoint.kind) {
    case 'verified': return checkpoint.cursor;
    // Older bundles conflated delivery with reconciliation. Their cached history
    // needs replay/overflow invalidation; a new live-only cache has no such history.
    case 'legacy': return '0';
    case 'cold':
    case 'bootstrap': return undefined;
  }
}

function earlierSyncCursor(left: SyncCursor, right: SyncCursor): SyncCursor {
  return compareSyncCursors(left, right) <= 0 ? left : right;
}
